/**
 * Auth context — manages Firebase Auth state and the admin custom claim.
 *
 * The admin dashboard uses Firebase Auth (email link + email/password) on the
 * lift-assisted-app project. Admins are users with the `admin: true`
 * custom claim, set via the Firebase Admin SDK.
 *
 * This context exposes:
 *   - `user`: the current Firebase user (or null)
 *   - `isAdmin`: whether the user has the admin custom claim
 *   - `loading`: true while the initial auth state resolves
 *   - `signIn(email, password)`: email/password sign-in
 *   - `sendSignInLink(email)`: send a passwordless email link
 *   - `signOut()`: sign out and clear state
 *   - `refreshClaims()`: force-refresh the ID token to pick up claim changes
 */
import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from 'react';
import {
  signInWithEmailAndPassword,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  getIdTokenResult,
  type User,
} from 'firebase/auth';
import { getFirebaseAuth } from './config';

import { PREVIEW_MODE, PREVIEW_SIGNED_OUT } from '../preview-mode';

/** A mock admin user for preview/screenshot mode. */
const PREVIEW_USER = {
  uid: 'preview-admin',
  email: 'admin@blackramlabs.com',
  getIdToken: () => Promise.resolve('preview-token'),
} as unknown as User;

interface AuthContextValue {
  user: User | null;
  isAdmin: boolean;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  sendSignInLink: (email: string) => Promise<void>;
  completeSignInWithEmailLink: (email: string) => Promise<void>;
  isEmailLinkRedirect: boolean;
  signOut: () => Promise<void>;
  refreshClaims: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** Hook to access the auth context. Throws if used outside the provider. */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}

/** Provider that wraps the app and exposes auth state via useAuth(). */
export function AuthProvider({ children }: { children: ReactNode }) {
  const auth = getFirebaseAuth();
  const [user, setUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isEmailLinkRedirect, setIsEmailLinkRedirect] = useState(false);

  // Check the admin custom claim on the user's ID token.
  const checkAdminClaim = useCallback(async (currentUser: User) => {
    try {
      const tokenResult = await getIdTokenResult(currentUser, true);
      return Boolean(tokenResult.claims.admin);
    } catch (err) {
      console.error('[Auth] Failed to read custom claims:', err);
      return false;
    }
  }, []);

  useEffect(() => {
    if (PREVIEW_MODE) {
      // 'signedout' starts unauthenticated so the real login form and
      // redirect run — signIn()/sendSignInLink() set the mock admin.
      if (!PREVIEW_SIGNED_OUT) {
        setUser(PREVIEW_USER);
        setIsAdmin(true);
      }
      // Mirror the real isSignInWithEmailLink() check so the email-link
      // redirect states (including the cross-device complete-sign-in
      // flow, LA-827) are exercisable in preview mode:
      // /login?mode=signIn&oobCode=… reads as an email-link arrival.
      const linkParams = new URLSearchParams(window.location.search);
      if (linkParams.get('mode') === 'signIn' && linkParams.has('oobCode')) {
        setIsEmailLinkRedirect(true);
      }
      setLoading(false);
      return;
    }

    // Check if we arrived via an email link redirect.
    if (isSignInWithEmailLink(auth, window.location.href)) {
      setIsEmailLinkRedirect(true);
    }

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        const admin = await checkAdminClaim(currentUser);
        setUser(currentUser);
        setIsAdmin(admin);
      } else {
        setUser(null);
        setIsAdmin(false);
      }
      setLoading(false);
    });
    return unsubscribe;
  }, [auth, checkAdminClaim]);

  const signIn = useCallback(
    async (email: string, password: string) => {
      if (PREVIEW_MODE) {
        setUser(PREVIEW_USER);
        setIsAdmin(true);
        return;
      }
      const cred = await signInWithEmailAndPassword(auth, email, password);
      const admin = await checkAdminClaim(cred.user);
      setUser(cred.user);
      setIsAdmin(admin);
    },
    [auth, checkAdminClaim],
  );

  const sendSignInLink = useCallback(
    async (email: string) => {
      if (PREVIEW_MODE) {
        setUser(PREVIEW_USER);
        setIsAdmin(true);
        return;
      }
      const actionCodeSettings = {
        url: window.location.origin + '/login',
        handleCodeInApp: true,
      };
      await sendSignInLinkToEmail(auth, email, actionCodeSettings);
      // Store email so we can retrieve it when the user returns via the link.
      window.localStorage.setItem('emailForSignIn', email);
    },
    [auth],
  );

  const completeSignInWithEmailLink = useCallback(
    async (email: string) => {
      if (PREVIEW_MODE) {
        setUser(PREVIEW_USER);
        setIsAdmin(true);
        setIsEmailLinkRedirect(false);
        return;
      }
      const cred = await signInWithEmailLink(auth, email, window.location.href);
      window.localStorage.removeItem('emailForSignIn');
      const admin = await checkAdminClaim(cred.user);
      setUser(cred.user);
      setIsAdmin(admin);
      setIsEmailLinkRedirect(false);
    },
    [auth, checkAdminClaim],
  );

  const signOut = useCallback(async () => {
    if (PREVIEW_MODE) {
      setUser(null);
      setIsAdmin(false);
      return;
    }
    await firebaseSignOut(auth);
    setUser(null);
    setIsAdmin(false);
  }, [auth]);

  const refreshClaims = useCallback(async () => {
    if (user) {
      const admin = await checkAdminClaim(user);
      setIsAdmin(admin);
    }
  }, [user, checkAdminClaim]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAdmin,
        loading,
        signIn,
        sendSignInLink,
        completeSignInWithEmailLink,
        isEmailLinkRedirect,
        signOut,
        refreshClaims,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
